import { useQuery, useInfiniteQuery, useMutation } from '@tanstack/react-query';
import type { CurrentRead } from '@signalvote/data';
import type {
  PaymentsStatusDto,
  StartOnboardingRequest,
  SetSlugRequest,
  AccountSessionDto,
  DonationDashboardSummaryDto,
  DonationFeedDto,
  DonationDetailDto,
  DonationStatusTab,
  NeedsAttentionDto,
  StripeBalanceDto,
  StripePayoutScheduleDto,
  StripePayoutsDto,
  RefundDonationResponseDto,
  DonationConfigDto,
  SaveDonationConfigRequest,
  DonationLogoUploadUrlDto,
  DonationLogoConfirmedDto,
  DonationLogoConfirmRequest,
  DonationLogoUploadUrlRequest,
} from '@signalvote/shared';
import { donationLogoError, donationLogoDimensionError, failureFromResponse } from '@signalvote/shared';
import { readImageDimensions } from '@/lib/imageDimensions';
import { authService } from '../../services/authService';
import { apiJson } from './apiFetch';
import type { ApiErrorSpec } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import { campaignHeaders, defineInfiniteQuery, defineQuery, defineWrite, every } from '../../lib/data';

const PAYMENTS_PATH = '/api/finance/payments';

/** The response as an `ApiFailure` carrying the server's `{ error }` sentence, or the fallback. */
function serverSentence(fallback: string): ApiErrorSpec {
  return (body, response) => failureFromResponse(response, body, body.error || fallback);
}

// ============================================================================
// Query Hooks
// ============================================================================

/** The donation account's status and the donation page's slug and publish state. */
export const paymentsStatusRead = defineQuery({
  key: () => ['finance', 'payments', 'status'],
  reads: 'GET /api/finance/payments/status',
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<PaymentsStatusDto>(`${PAYMENTS_PATH}/status`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch payments status',
    }),
});

export function usePaymentsStatus(): CurrentRead<PaymentsStatusDto> {
  return paymentsStatusRead(undefined);
}

/**
 * The donation-page "Advanced settings": the saved custom suggested amounts (empty when none set),
 * header message and logo. The editor seeds its initial state from this.
 */
export const donationConfigRead = defineQuery({
  key: () => ['finance', 'payments', 'donation-config'],
  reads: 'GET /api/finance/payments/donation-config',
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<DonationConfigDto>(`${PAYMENTS_PATH}/donation-config`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to load donation settings',
    }),
});

export function useDonationConfig(): CurrentRead<DonationConfigDto> {
  return donationConfigRead(undefined);
}

/**
 * The post-onboarding donation dashboard summary: account health, donations and fees this period,
 * all from our own DB, so it never blocks the health banner on a live Stripe call.
 */
export const donationSummaryRead = defineQuery({
  key: () => ['finance', 'payments', 'summary'],
  reads: 'GET /api/finance/payments/summary',
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<DonationDashboardSummaryDto>(`${PAYMENTS_PATH}/summary`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch donation summary',
    }),
});

export function useDonationSummary(): CurrentRead<DonationDashboardSummaryDto> {
  return donationSummaryRead(undefined);
}

/** The recent-donations panel: the first page of portal donations, newest first. */
export const donationsFeedRead = defineQuery({
  key: () => ['finance', 'payments', 'donations'],
  reads: 'GET /api/finance/payments/donations',
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<DonationFeedDto>(`${PAYMENTS_PATH}/donations`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch recent donations',
    }),
});

export function useDonationsFeed(): CurrentRead<DonationFeedDto> {
  return donationsFeedRead(undefined);
}

/**
 * The "View all" feed: keyset pages of portal donations for one status tab, following the opaque
 * `nextCursor`. Each tab is its own cache entry.
 */
export const useDonationsFeedPaged = defineInfiniteQuery({
  key: (tab: DonationStatusTab) => ['finance', 'payments', 'donations', 'feed', tab],
  reads: 'GET /api/finance/payments/donations',
  fetch: (tab, cursor: string | null, { signal, campaignId }) => {
    const params = new URLSearchParams();
    // 'all' is the server default, so it rides as no ?status= param.
    if (tab !== 'all') params.set('status', tab);
    if (cursor) params.set('cursor', cursor);
    const qs = params.toString();
    return apiJson<DonationFeedDto>(`${PAYMENTS_PATH}/donations${qs ? `?${qs}` : ''}`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch donations',
    });
  },
  initialCursor: null,
  nextCursor: (last) => last.nextCursor ?? undefined,
  items: (page) => page.items,
});

/** One portal donation's full record for the detail drawer. */
export const useDonationDetail = defineQuery({
  key: (contributionId: string) => ['finance', 'payments', 'donations', 'detail', contributionId],
  reads: 'GET /api/finance/payments/donations/{contributionId}',
  fetch: (contributionId, { signal, campaignId }) =>
    apiJson<DonationDetailDto>(`${PAYMENTS_PATH}/donations/${contributionId}`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch the donation',
    }),
});

/** Open disputes on portal donations. Empty `disputes` means nothing to surface. */
export const needsAttentionRead = defineQuery({
  key: () => ['finance', 'payments', 'needs-attention'],
  reads: 'GET /api/finance/payments/needs-attention',
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<NeedsAttentionDto>(`${PAYMENTS_PATH}/needs-attention`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch donations needing attention',
    }),
});

export function useNeedsAttention(): CurrentRead<NeedsAttentionDto> {
  return needsAttentionRead(undefined);
}

// The "available to pay out" panel — a LIVE connected-account balance read (never
// our DB, never persisted per ADR-011). Separate from the DB summary so the page and
// the deadline-critical health banner never block on Stripe; a failure surfaces as
// `isError` → an inline retry in the panel.
export function useStripeBalance(enabled = true) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.payments.balance(),
    queryFn: () =>
      apiJson<StripeBalanceDto>(`${PAYMENTS_PATH}/balance`, {
        error: 'Failed to fetch available balance',
      }),
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The connected account's actual payout cadence from live Stripe Balance Settings.
// Read-only and independently retryable; callers never infer a fallback schedule.
export function useStripePayoutSchedule(enabled = true) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.payments.payoutSchedule(),
    queryFn: () =>
      apiJson<StripePayoutScheduleDto>(`${PAYMENTS_PATH}/payout-schedule`, {
        error: 'Failed to fetch payout schedule',
      }),
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The payouts panel — LIVE connected-account payouts, newest first. An infinite query:
// the landing shows the first page (~20); "view all" pages full history by following
// Stripe's `starting_after` cursor (`nextCursor`). Shared with the next-payout hero
// (same key → one fetch). Live-only, never persisted (ADR-011).
export function useStripePayouts(enabled = true) {
  const { selectedCampaign } = useCampaignStore();

  return useInfiniteQuery({
    queryKey: queryKeys.finance.payments.payouts(),
    queryFn: async ({ pageParam }) => {
      const suffix = pageParam
        ? `?starting_after=${encodeURIComponent(pageParam)}`
        : '';
      return apiJson<StripePayoutsDto>(`${PAYMENTS_PATH}/payouts${suffix}`, {
        error: 'Failed to fetch payouts',
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Creates the connected account and takes the donation page live. Saved through the request, so
 * its write set refreshes the status.
 */
export const useStartOnboarding = defineWrite({
  send: (data: StartOnboardingRequest) =>
    apiJson<PaymentsStatusDto>(`${PAYMENTS_PATH}/account`, {
      method: 'POST',
      json: data,
      error: serverSentence('Could not start setup'),
    }),
});

// Imperative mint of a Connect account session — the client secret is
// short-lived and single-use, so this is a mutation with NO cache entry. The
// page mints once to read the publishable key + first client secret, then hands
// `fetchClientSecret` (which re-mints) to the embedded component.
export function useCreateAccountSession() {
  return useMutation({
    mutationFn: () =>
      apiJson<AccountSessionDto>(`${PAYMENTS_PATH}/account-session`, {
        method: 'POST',
        error: (body) => new Error(body.error || 'Could not connect to our payments partner'),
      }),
  });
}

// The server's sentence is already treasurer-safe ("That link is taken", "Locked once published").
export const useSetDonationSlug = defineWrite({
  send: (data: SetSlugRequest) =>
    apiJson<PaymentsStatusDto>(`${PAYMENTS_PATH}/slug`, {
      method: 'PUT',
      json: data,
      error: serverSentence('Could not save your link'),
    }),
});

/**
 * A full refund of a portal donation (Finance:Manage only; the endpoint enforces it). A refusal
 * carries the server's `code`: `insufficient_balance` (already paid out) routes to the support hatch.
 */
export const useRefundDonation = defineWrite({
  send: (contributionId: string) =>
    apiJson<RefundDonationResponseDto>(`${PAYMENTS_PATH}/donations/${contributionId}/refund`, {
      method: 'POST',
      error: serverSentence('We couldn’t complete this refund.'),
    }),
  // A refund that loses the race to its charge.refunded webhook answers success with nothing saved,
  // so its write set is empty while the gift's status, the totals and the disputes have moved.
  onSuccess: (_result, contributionId, refreshRead) => {
    void refreshRead(donationsFeedRead, undefined);
    void refreshRead(useDonationsFeedPaged, every);
    void refreshRead(useDonationDetail, contributionId);
    void refreshRead(donationSummaryRead, undefined);
    void refreshRead(needsAttentionRead, undefined);
  },
});

// Last write wins, no concurrency token (a single admin's config). The server's validation sentence is
// treasurer-safe.
export const useSaveDonationConfig = defineWrite({
  send: (data: SaveDonationConfigRequest) =>
    apiJson<DonationConfigDto>(`${PAYMENTS_PATH}/donation-config`, {
      method: 'PUT',
      json: data,
      error: serverSentence('Could not save your donation settings'),
    }),
});

// Upload a campaign logo for the donation page. Orchestrates the presigned flow: validate client-side
// (mirrors the server bounds), mint a short-lived presigned PUT URL, then upload the binary DIRECTLY to
// object storage (prod) or to the API's local-upload endpoint (dev). Returns the VERIFIED descriptor so
// the caller stashes a storage key whose bytes the server has read back and approved, and shows its
// public URL as a preview.

/** Unwraps the API's `{ error }` envelope, falling back when the body is missing or not JSON. */
function donationLogoFailure(fallback: string): ApiErrorSpec {
  return (body) => new Error(body.error || fallback);
}

export function useUploadDonationLogo() {
  return useMutation({
    mutationFn: async (file: File): Promise<DonationLogoConfirmedDto> => {
      const clientError = donationLogoError({ type: file.type, size: file.size });
      if (clientError) throw new Error(clientError);

      // Measure before uploading. Bytes say nothing about what an image costs a donor's phone to
      // decode, and catching it here saves the admin a pointless round trip; the server re-checks the
      // stored object's real dimensions regardless.
      const dimensionError = donationLogoDimensionError(await readImageDimensions(file));
      if (dimensionError) throw new Error(dimensionError);

      const upload = await apiJson<DonationLogoUploadUrlDto>(
        `${PAYMENTS_PATH}/donation-logo/upload-url`,
        {
          method: 'POST',
          json: {
            fileName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
          } satisfies DonationLogoUploadUrlRequest,
          error: donationLogoFailure('Could not prepare the logo upload'),
        },
      );

      // No ACL header on either path: the URL targets the staging area, whose bytes are not verified yet
      // and must not be publicly readable — the server applies public-read when it copies the verified
      // image out. Both calls are written out rather than picked into a variable, because `fetch` and
      // `authenticatedFetch` both lose their `this` when detached from their receiver.
      const putRequest: RequestInit = {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      };
      const put = upload.usesPresignedUrls
        // Direct-to-storage: auth rides in the presigned URL's query string, so an Authorization header
        // would be rejected as a signature mismatch — this one deliberately skips authenticatedFetch.
        ? await fetch(upload.uploadUrl, putRequest)
        // Local dev: the upload goes to the API, which needs the auth/tenant headers.
        : await authService.authenticatedFetch(upload.uploadUrl, putRequest);
      if (!put.ok) throw new Error('The logo upload failed. Please try again.');

      // Nothing so far proves what actually landed in storage: the presigned PUT constrains neither
      // length nor content, and every check up to here ran on this side of the wire. The server reads the
      // object back and, if it passes, copies it to a durable key this upload URL cannot reach — so the
      // key we preview and save is one whose bytes were verified and can no longer be swapped.
      return apiJson<DonationLogoConfirmedDto>(`${PAYMENTS_PATH}/donation-logo/confirm`, {
        method: 'POST',
        json: { storageKey: upload.storageKey } satisfies DonationLogoConfirmRequest,
        error: donationLogoFailure('That logo could not be verified. Please try a different image.'),
      });
    },
  });
}

export const usePublishDonationPage = defineWrite({
  send: () =>
    apiJson<PaymentsStatusDto>(`${PAYMENTS_PATH}/publish`, {
      method: 'POST',
      error: serverSentence('Could not publish your donation page'),
    }),
});
